import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Trash2, Stamp } from "lucide-react";
import { api, authHeaders, fmtError } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "");

export function InboxTab() {
  const [msgs, setMsgs] = useState([]);
  const [stamps, setStamps] = useState([]);
  const h = { headers: authHeaders() };

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([axios.get(api("/api/admin/messages"), { headers: authHeaders() }), axios.get(api("/api/guestbook"))]);
      setMsgs(m.data);
      setStamps(s.data);
    } catch (e) {
      toast.error(fmtError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (m) => {
    if (!m.read) { await axios.put(api(`/api/admin/messages/${m.id}/read`), {}, h); load(); }
  };
  const del = async (url, label) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    await axios.delete(api(url), h);
    load();
  };

  return (
    <div data-testid="tab-inbox" className="grid lg:grid-cols-2 gap-10">
      <div>
        <h2 className="text-3xl font-black mb-1 flex items-center gap-2"><Mail size={22} /> Messages</h2>
        <p className="text-sm text-black/60 mb-5">Everything sent from the "Say hi" form. {msgs.filter((m) => !m.read).length} unread.</p>
        {msgs.length === 0 && <p className="text-sm text-black/50">No messages yet.</p>}
        <div className="space-y-3">
          {msgs.map((m) => (
            <details key={m.id} onToggle={(e) => e.currentTarget.open && open(m)} className="border border-black/15 p-4" data-testid={`message-${m.id}`}>
              <summary className="cursor-pointer flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className={`block truncate ${m.read ? "font-medium" : "font-black"}`}>{!m.read && <span className="inline-block w-2 h-2 rounded-full bg-[#9E3B4B] mr-2" />}{m.name} · {m.email}</span>
                  <span className="text-xs text-black/50">{when(m.created_at)}</span>
                </span>
              </summary>
              <p className="mt-3 text-sm whitespace-pre-wrap">{m.message}</p>
              <div className="flex gap-3 mt-4">
                <a href={`mailto:${m.email}`} className="text-xs font-bold uppercase tracking-wider underline">Reply by email</a>
                <button onClick={() => del(`/api/admin/messages/${m.id}`, "message")} className="text-xs font-bold uppercase tracking-wider text-red-700 inline-flex items-center gap-1" data-testid={`message-delete-${m.id}`}><Trash2 size={12} /> Delete</button>
              </div>
            </details>
          ))}
        </div>
      </div>
      <div>
        <h2 className="text-3xl font-black mb-1 flex items-center gap-2"><Stamp size={22} /> Visitor stamps</h2>
        <p className="text-sm text-black/60 mb-5">Stamps appear on the site instantly. Delete any you don't want.</p>
        {stamps.length === 0 && <p className="text-sm text-black/50">No stamps yet.</p>}
        <div className="divide-y divide-black/10 border border-black/15">
          {stamps.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3" data-testid={`stamp-row-${s.id}`}>
              <div>
                <p className="font-bold">{s.company}</p>
                <p className="text-xs text-black/50">{s.name || "—"} · {when(s.created_at)}</p>
              </div>
              <button onClick={() => del(`/api/admin/guestbook/${s.id}`, "stamp")} className="p-2 text-red-700" data-testid={`stamp-delete-${s.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
